import React, { useState, useEffect } from 'react';
import { Eye, Edit, Trash2, ChevronDown, ChevronRight, MapPin } from 'lucide-react';
import { Button } from '../../../../../../components/common/Button';
import { POI_TYPES } from '../../../../../../constants/poiTypes';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../../../config/firebase';
import { logError, logInfo } from '../../../../../../utils/logger';

const POIsTab = ({ map, onVisibilityChange, visibilityState = {}, pois = [], activeSurvey }) => {
  const [expandedTypes, setExpandedTypes] = useState({});
  const [localVisibility, setLocalVisibility] = useState(visibilityState?.poiTypes || {});

  // Sync local visibility state with parent when it changes
  useEffect(() => {
    setLocalVisibility(visibilityState?.poiTypes || {});
  }, [visibilityState?.poiTypes]);

  // Group POIs by type
  const poisByType = pois.reduce((acc, poi) => {
    if (!acc[poi.type]) {
      acc[poi.type] = [];
    }
    acc[poi.type].push(poi);
    return acc;
  }, {});

  const handleVisibilityToggle = (type) => {
    if (onVisibilityChange) {
      const currentVisibility = localVisibility[type] !== false;
      const newVisibility = {
        ...localVisibility,
        [type]: !currentVisibility
      };
      
      // Update local state immediately for responsive UI
      setLocalVisibility(newVisibility);
      
      // Notify parent component
      onVisibilityChange('poiTypes', newVisibility);
      
      // Store visibility state in localStorage
      localStorage.setItem('poiVisibility', JSON.stringify({
        surveyId: activeSurvey.id,
        visibility: newVisibility
      }));
      
      logInfo(`POI type visibility toggled: ${type}`);
    }
  };

  // Load saved visibility state on mount
  useEffect(() => {
    if (activeSurvey?.id) {
      try {
        const savedState = localStorage.getItem('poiVisibility');
        if (savedState) {
          const { surveyId, visibility } = JSON.parse(savedState);
          if (surveyId === activeSurvey.id) {
            setLocalVisibility(visibility);
            onVisibilityChange?.('poiTypes', visibility);
          }
        }
      } catch (error) {
        logError('Failed to load POI visibility state:', error);
      }
    }
  }, [activeSurvey?.id]);

  const handleDelete = async (poiId) => {
    if (!window.confirm('Are you sure you want to delete this POI?')) return;
    
    try {
      await updateDoc(doc(db, 'pois', poiId), {
        deleted: true,
        updatedAt: new Date().toISOString()
      });
      logInfo('POI deleted successfully');
    } catch (error) {
      logError('Failed to delete POI:', error);
    }
  };

  const handleExpand = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const formatCoordinate = (value) => {
    return typeof value === 'number' ? value.toFixed(6) : 'N/A';
  };

  if (!activeSurvey) {
    return (
      <div className="p-4 text-center text-gray-400">
        No active survey selected
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-medium text-white">Points of Interest</h2>
      
      <div className="space-y-2">
        {Object.entries(POI_TYPES).map(([type, config]) => {
          const poisOfType = poisByType[type] || [];
          if (poisOfType.length === 0) return null;

          const Icon = config.icon;
          const isVisible = localVisibility[type] !== false;
          const isExpanded = expandedTypes[type];

          return (
            <div key={type} className="bg-gray-700/50 rounded-lg overflow-hidden">
              {/* Type Header */}
              <div className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleExpand(type)}
                    className="p-1 hover:bg-gray-600 rounded"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded" style={{ backgroundColor: `${config.color}20` }}>
                      <Icon className="w-4 h-4" style={{ color: config.color }} />
                    </div>
                    <span className="text-white font-medium">{type}</span>
                    <span className="text-sm text-gray-400">({poisOfType.length})</span>
                  </div>
                </div>
                <Button
                  onClick={() => handleVisibilityToggle(type)}
                  className={`p-1.5 rounded transition-colors ${
                    isVisible 
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                      : 'bg-gray-600/20 text-gray-400 hover:bg-gray-600/30'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                </Button>
              </div>

              {/* POI List */}
              {isExpanded && (
                <div className="border-t border-gray-600">
                  {poisOfType.map((poi) => (
                    <div 
                      key={poi.id}
                      className="p-3 border-b border-gray-600 last:border-b-0 hover:bg-gray-600/30"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-white">
                              {config.prefix}-{poi.id.slice(-8)}
                            </div>
                            <div className="text-xs text-gray-400">
                              {poi.notes || `${formatCoordinate(poi.coordinates?.latitude)}, ${formatCoordinate(poi.coordinates?.longitude)}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            onClick={() => {
                              if (map && poi.coordinates?.latitude && poi.coordinates?.longitude) {
                                map.panTo({
                                  lat: Number(poi.coordinates.latitude),
                                  lng: Number(poi.coordinates.longitude)
                                });
                                map.setZoom(18);
                              }
                            }}
                            className="p-1.5 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded"
                          >
                            <MapPin className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => {/* Handle edit */}}
                            className="p-1.5 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(poi.id)}
                            className="p-1.5 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(poisByType).length === 0 && (
          <div className="text-center text-gray-400 py-4">
            No POIs found
          </div>
        )}
      </div>
    </div>
  );
};

export default POIsTab;